import { getCSSValue, getUniqueKey, deleteUniqueKeyName } from 'utils'

function getRuleMap(myWindow: Window): any {
  const sheets = myWindow.document.styleSheets

  const roleMap = {}

  const applyStyleRule = (styleRule: CSSStyleRule) => {
    const elementList = myWindow.document.querySelectorAll(styleRule.selectorText)
    elementList.forEach((element) => {
      const niElement = element as any
      if (niElement.niUniqueKey == null) {
        niElement.niUniqueKey = getUniqueKey('styleToInline')
      }
      if (roleMap[niElement.niUniqueKey] == null) {
        roleMap[niElement.niUniqueKey] = []
      }
      roleMap[niElement.niUniqueKey].push(styleRule)
    })
  }

  Array.from(sheets).forEach((sheet) => {
    let isAccessible = true
    if (sheet.href != null && sheet.href.length > 0) {
      //external css인 경우.
      try {
        sheet.cssRules
        isAccessible = true
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
      } catch (e: any) {
        // console.log(' --- access error : ', e)
        isAccessible = false
      }
    }

    if (isAccessible === true) {
      const rules = Array.from(sheet.cssRules)
      rules.forEach((rule /* , idx */) => {
        const ruleType = rule.constructor.name
        if (ruleType === 'CSSStyleRule') {
          const styleRule = rule as CSSStyleRule
          applyStyleRule(styleRule)
        } else if (ruleType === 'CSSMediaRule') {
          let mediaRule = rule as CSSMediaRule
          // console.info(' --- mediaquery : ', mediaRule.conditionText)
          if (myWindow.matchMedia(mediaRule.conditionText).matches === true) {
            const mediaCssRules = Array.from(mediaRule.cssRules)
            mediaCssRules.forEach((mediaRule) => {
              if (mediaRule.constructor.name === 'CSSStyleRule') {
                const styleRule = mediaRule as CSSStyleRule
                applyStyleRule(styleRule)
              }
            })
          }
        } else if (ruleType === 'CSSUnknownRule') {
          console.log(' --- unknown rule : ', rule.constructor.name)
        }
      })
    } else {
      console.log(' --- inaccessible external sheet : ', sheet.href)

      const elem = sheet.ownerNode as HTMLLinkElement
      elem.disabled = true
    }
  })

  return roleMap
}

function matchRules(el: Element, ruleMap: any): CSSStyleRule[] {
  let ret = []

  const niElement = el as any
  const key = niElement.niUniqueKey

  if (key != null && ruleMap[key] != null) {
    const rules = ruleMap[key]
    rules.forEach((rule) => {
      ret.push(rule)
    })
  }

  sortBySpecificity(el, ret)

  return ret
}

function _find(string, re) {
  var matches = string.match(re)
  return matches ? matches.length : 0
}

const ELEMENT_RE = /[\w-]+/g
const ID_RE = /#[\w-]+/g
const CLASS_RE = /\.[\w-]+/g
const ATTR_RE = /\[[^\]]+\]/g
// :not() pseudo-class does not add to specificity, but its content does as if it was outside it
const PSEUDO_CLASSES_RE = /\:(?!not)[\w-]+(\(.*\))?/g
const PSEUDO_ELEMENTS_RE = /\:\:?(after|before|first-letter|first-line|selection)/g

// calculates the specificity of a given `selector`
function calculateScore(selector) {
  var score = [0, 0, 0],
    parts = selector.split(' '),
    part,
    match
  //TODO: clean the ':not' part since the last ELEMENT_RE will pick it up
  while (((part = parts.shift()), typeof part == 'string')) {
    // find all pseudo-elements
    match = _find(part, PSEUDO_ELEMENTS_RE)
    score[2] += match
    // and remove them
    match && (part = part.replace(PSEUDO_ELEMENTS_RE, ''))
    // find all pseudo-classes
    match = _find(part, PSEUDO_CLASSES_RE)
    score[1] += match
    // and remove them
    match && (part = part.replace(PSEUDO_CLASSES_RE, ''))
    // find all attributes
    match = _find(part, ATTR_RE)
    score[1] += match
    // and remove them
    match && (part = part.replace(ATTR_RE, ''))
    // find all IDs
    match = _find(part, ID_RE)
    score[0] += match
    // and remove them
    match && (part = part.replace(ID_RE, ''))
    // find all classes
    match = _find(part, CLASS_RE)
    score[1] += match
    // and remove them
    match && (part = part.replace(CLASS_RE, ''))
    // find all elements
    score[2] += _find(part, ELEMENT_RE)
  }
  return parseInt(score.join(''), 10)
}

// returns the heights possible specificity score an element can get from a give rule's selectorText
function getSpecificityScore(element, selector_text) {
  var selectors = selector_text.split(','),
    selector,
    score,
    result = 0
  while ((selector = selectors.shift())) {
    if (element.matches(selector) === true) {
      score = calculateScore(selector)
      result = score > result ? score : result
    }
  }
  return result
}

function sortBySpecificity(element, rules) {
  // comparing function that sorts CSSStyleRules according to specificity of their `selectorText`
  function compareSpecificity(a, b) {
    return (
      getSpecificityScore(element, b.selectorText) - getSpecificityScore(element, a.selectorText)
    )
  }
  return rules.sort(compareSpecificity)
}

function cloneElem(element: HTMLElement | SVGElement): HTMLElement | SVGElement {
  if (element.tagName === 'BODY') {
    const newDiv = document.createElement('DIV')
    let attr
    let attributes = Array.from(element.attributes)
    try {
      while ((attr = attributes.pop())) {
        newDiv.setAttribute(attr.nodeName, attr.nodeValue)
      }
    } catch (e) {
      console.log(' --- cloneElem error : ', e)
    }
    return newDiv
  }

  return element.cloneNode(false) as HTMLElement
}

interface CheckResult {
  prop: string
  value: any
  isImportant: boolean
}

// 셀렉터에 의사요소(::before/::after 등)가 포함됐는지 판별 (비-global — test lastIndex 부작용 없음)
const PSEUDO_ELEMENT_MATCH = /::?(?:before|after|first-letter|first-line|selection)\b/i

interface PseudoDecl {
  value: string
  important: boolean
}

/**
 * 의사요소 규칙의 셀렉터를 뷰어 출력 형태에 맞게 재작성한다.
 * - class/id 토큰에 prefix 부여(styleToInlineStyle 의 clsPrefix/idPrefix 와 동일 규칙)
 * - 단일콜론 의사요소를 `::` 표준형으로 정규화(dedup 키 일관)
 *
 * 컨테이너 스코프(`[data-email-id] .htmlMailBody` / `.print-body`)는 여기서 붙이지 않는다.
 * 결과 셀렉터는 `blockquote.x_blockquote-quote::before` 처럼 **클래스 한정**이라 인용문/타 메일을
 * 오염시키지 않으며(idempotent), 스코프는 소비 컨텍스트(뷰어/print)에서 필요 시 prepend 한다.
 */
export function rewritePseudoSelector(
  selector: string,
  clsPrefix: string,
  idPrefix: string,
): string {
  let s = selector.trim().replace(/\s+/g, ' ')
  s = s.replace(/::?(before|after|first-letter|first-line|selection)\b/gi, '::$1')
  if (clsPrefix.length > 0) {
    s = s.replace(/\.(-?[_a-zA-Z][\w-]*)/g, (_m, name) => `.${clsPrefix}${name}`)
  }
  if (idPrefix.length > 0) {
    s = s.replace(/#(-?[_a-zA-Z][\w-]*)/g, (_m, name) => `#${idPrefix}${name}`)
  }
  return s
}

/**
 * 주입된 의사요소 `<style>`(marker 로 식별) 내용을 `@scope (<scopeRoot>) { … }` 로 감싸 적용 범위를
 * 한정한다. getSafeHTML 은 스코프 없이(클래스 한정) 의사요소 `<style>` 를 남기고, 소비 컨텍스트가
 * 이 함수로 스코프를 부여한다 — 뷰어는 `[data-email-id="{id}"] .htmlMailBody`, print 는 `.print-body`.
 *
 * 이미 `@scope (...)` 로 감싸진 경우(뷰어에서 감싼 것을 print 가 clone 한 경우 등)에는 scope root 만
 * 교체한다(재래핑 방지 + print 재스코프 동시 처리). 오류/빈 내용은 무해하게 건너뛴다.
 */
export function applyPseudoStyleScope(
  root: ParentNode,
  scopeRoot: string,
  marker = 'data-ngw-pseudo',
): void {
  root.querySelectorAll(`style[${marker}]`).forEach((styleEl) => {
    const css = styleEl.textContent ?? ''
    if (css.length === 0) return
    styleEl.textContent = /^\s*@scope\b/.test(css)
      ? css.replace(/@scope\s*\([^)]*\)/, `@scope (${scopeRoot})`)
      : `@scope (${scopeRoot}) {\n${css}\n}`
  })
}

/**
 * 문서의 모든 스타일시트에서 의사요소 규칙만 수집→셀렉터 재작성→동일 셀렉터 병합한 CSS 문자열 반환.
 * styleToInlineStyle 이 인라인화하지 못하는 `::before`/`::after` 장식을 뷰어에 보존하기 위한 것.
 * 순수 additive — 어떤 오류(cross-origin 시트, 파싱 실패 등)든 해당 규칙/시트만 건너뛴다.
 */
export function collectPseudoElementCSS(
  win: Window,
  clsPrefix: string,
  idPrefix: string,
  skipMarker: string,
): string {
  const merged = new Map<string, Map<string, PseudoDecl>>()
  const order: string[] = []

  const takeRule = (rule: CSSStyleRule): void => {
    const selectorText = rule.selectorText
    if (selectorText == null || PSEUDO_ELEMENT_MATCH.test(selectorText) === false) return
    const parts = selectorText
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0 && PSEUDO_ELEMENT_MATCH.test(p))
    if (parts.length === 0) return
    const style = rule.style
    for (const part of parts) {
      const sel = rewritePseudoSelector(part, clsPrefix, idPrefix)
      let decl = merged.get(sel)
      if (decl == null) {
        decl = new Map<string, PseudoDecl>()
        merged.set(sel, decl)
        order.push(sel)
      }
      for (const prop of Array.from(style)) {
        const important = style.getPropertyPriority(prop) === 'important'
        const prev = decl.get(prop)
        if (important === true || prev == null || prev.important === false) {
          decl.set(prop, { value: style.getPropertyValue(prop), important })
        }
      }
    }
  }

  const walk = (rules: CSSRuleList): void => {
    for (const rule of Array.from(rules)) {
      const type = rule.constructor.name
      if (type === 'CSSStyleRule') {
        try {
          takeRule(rule as CSSStyleRule)
        } catch {
          /* 개별 규칙 실패 skip */
        }
      } else if (type === 'CSSMediaRule') {
        try {
          const media = rule as CSSMediaRule
          if (win.matchMedia(media.conditionText).matches === true) {
            walk(media.cssRules)
          }
        } catch {
          /* skip */
        }
      }
    }
  }

  for (const sheet of Array.from(win.document.styleSheets)) {
    try {
      const owner = sheet.ownerNode as HTMLElement | null
      if (owner != null && skipMarker.length > 0 && owner.hasAttribute(skipMarker)) continue
      walk(sheet.cssRules)
    } catch {
      /* cross-origin 등 접근 불가 시트 skip */
    }
  }

  if (order.length === 0) return ''
  let css = ''
  for (const sel of order) {
    const decl = merged.get(sel)
    if (decl == null) continue
    let body = ''
    decl.forEach((info, prop) => {
      body += `${prop}:${info.value}${info.important ? ' !important' : ''};`
    })
    if (body.length > 0) css += `${sel}{${body}}\n`
  }
  return css
}

/**
 * A function that iterates through the computed style properties of a HTML
 * element and redefines them as inline styles.
 */
export default function styleToInlineStyle(
  element: HTMLElement | SVGElement,
  options?: {
    remSize?: number
    recursive?: boolean
    properties?: string[]
    clsPrefix?: string
    idPrefix?: string
    processElement: (element: Element) => void
    win?: Window
    shorthandCheck: null | ((element: Element, prop: string) => CheckResult)
    /** 지정 시 최상위 반환 요소에 의사요소(::before/::after) 보존용 `<style>` 을 주입한다(email id 기준 scope). */
    pseudoScopeId?: string
    /** 주입 `<style>` 식별 마커 속성명(기본 `data-ngw-pseudo`). getSafeHTML 의 `<style>` 제거에서 skip 대상. */
    pseudoStyleMarker?: string
  },
  defaultRuleMap?: any,
): HTMLElement | SVGElement {
  if (!element) {
    throw new Error('No element specified.')
  }

  const myWindow = options?.win || window

  // const computedStyle = myWindow.getComputedStyle(element)

  const ruleMap = defaultRuleMap || getRuleMap(myWindow)

  // 낮은 우선순위의 스타일부터 적용하기 위해 역순으로 정렬
  const matches = matchRules(element, ruleMap).reverse()

  // we need to preserve any pre-existing inline styles.
  const newElement = cloneElem(element)

  const mergeStyle = (styleMap, style: CSSStyleDeclaration) => {
    for (let prop of Array.from(style)) {
      if (options?.properties == null || options.properties.includes(prop) === true) {
        let propName = prop
        let propValue = null
        if (options?.shorthandCheck != null) {
          const checkResult = options.shorthandCheck(element, prop)
          if (checkResult != null) {
            propName = checkResult.prop
            propValue = {
              value: checkResult.value,
              isImportant: checkResult.isImportant,
            }
          }
        }

        if (propValue == null) {
          propValue = {
            value: style.getPropertyValue(prop),
            isImportant: style.getPropertyPriority(prop) === 'important',
          }
        }

        const prevStyleInfo = styleMap[propName]
        if (
          propValue.isImportant === true ||
          prevStyleInfo == null ||
          prevStyleInfo.isImportant === false
        ) {
          styleMap[propName] = propValue
        }
      }
    }
  }

  const styleMap = {}
  matches.forEach((rule) => {
    mergeStyle(styleMap, rule.style)
  })

  mergeStyle(styleMap, element.style)

  let styleStr = ''
  for (let prop in styleMap) {
    const { value, isImportant } = styleMap[prop]
    // @todo - 'html-react-parser'에서 important 처리가 안되는 문제가 있어서 important를 제거하고 처리함.
    // newElement.style.setProperty(prop, value, isImportant ? 'important' : '');
    let newValue = value
    if (options?.remSize != null && typeof value === 'string' && value.endsWith('rem') === true) {
      const remValue = getCSSValue(value, false, options.remSize)
      if (remValue.type == 1 && remValue.unit === 'px') {
        newValue = remValue.number + 'px'
      }
    }
    // console.log(' --- prop : ', prop, newValue)
    // newElement.style.setProperty(prop, newValue, '')

    const importantStr = isImportant ? ' !important' : ''
    styleStr += `${prop}: ${newValue}${importantStr};`
  }

  if (styleStr.length > 0) {
    newElement.setAttribute('style', styleStr)
  }

  // if (element.className.indexOf('stb-left-cell') >= 0) {
  //   console.log(' --- element : ', element.style.cssText)
  //   console.log(' --- newElement : ', newElement.style.cssText)
  // }

  if (options?.recursive) {
    Array.from(element.childNodes).forEach((node) => {
      let newNode = null
      if (node.nodeType === Node.ELEMENT_NODE) {
        newNode = styleToInlineStyle(node as HTMLElement, options, ruleMap)
      } else {
        newNode = node.cloneNode(false)
      }
      newElement.appendChild(newNode)
    })
  }

  // if (srcRules.cssText.trim() !== '') {
  //   element.style.cssText = srcRules.cssText
  // }

  if (options?.clsPrefix && newElement.classList.length > 0) {
    const clsList = Array.from(newElement.classList).map((clsName) => {
      return options.clsPrefix + clsName
    })

    if (clsList.length > 0) {
      newElement.setAttribute('class', clsList.join(' '))
    }
  }

  const id = newElement.getAttribute('id')
  if (options?.idPrefix && id != null) {
    const newId = options.idPrefix + id

    newElement.setAttribute('id', newId)
  }

  if (options?.processElement) {
    options.processElement(newElement as Element)
  }

  if (defaultRuleMap == null) {
    deleteUniqueKeyName('styleToInline')
  }

  // 최상위 호출에서만: 의사요소(::before/::after) 규칙을 분리해 재작성한 <style> 를 반환 트리에 주입.
  // styleToInlineStyle 은 계산된 스타일만 인라인화하므로 의사요소는 인라인 불가 → 별도 <style> 로 보존.
  // 순수 additive — 실패 시 <style> 미주입(기존 동작 유지).
  if (
    defaultRuleMap == null &&
    options?.pseudoScopeId != null &&
    options.pseudoScopeId.length > 0
  ) {
    try {
      const marker = options.pseudoStyleMarker ?? 'data-ngw-pseudo'
      const css = collectPseudoElementCSS(
        myWindow,
        options.clsPrefix ?? '',
        options.idPrefix ?? '',
        marker,
      )
      if (css.length > 0) {
        const ownerDoc = newElement.ownerDocument
        if (ownerDoc != null) {
          // 컨테이너 스코프는 붙이지 않는다(클래스 한정 셀렉터라 오염 없음). 소비 컨텍스트에서 필요 시
          // prepend: 뷰어는 그대로(unscoped·idempotent), print 는 `.print-body` 를 추가한다.
          // 주입 <style> 는 marker 로만 식별(getSafeHTML 의 <style> 제거에서 skip 대상).
          const styleEl = ownerDoc.createElement('style')
          styleEl.setAttribute(marker, '')
          styleEl.textContent = css
          newElement.appendChild(styleEl)
        }
      }
    } catch {
      /* graceful degrade: 의사요소 <style> 미주입 */
    }
  }

  return newElement
}
